import { Typography } from "@mui/material";
import { 
  GridActionsCellItem, 
  GridActionsColDef, 
  GridColumns, 
  GridRenderCellParams, 
  GridValueGetterParams 
} from "@mui/x-data-grid";
import { CustomLoadingButton, CustomLoadingButtonProps } from "src/careflow/common/CustomButton";
import { getCodeableConceptDisplayName } from "src/careflow/helpers/common";
import { WrappedOrganization } from "src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization";
import { SearchFieldType } from "src/careflow/ph-core/grid/hook/useGridFilters";
import DataGridWithFilters from "src/careflow/ph-core/grid/react/DataGridWithFilters/DataGridWithFilters";
import { spreadSxProp } from "src/utils/cssStyles";
import uuidv4 from "src/utils/uuidv4";

interface CreateManuallyButtonProps extends CustomLoadingButtonProps {
  title
}

export const CreateManuallyButton = ({ sx, title, ...buttonProps }: CreateManuallyButtonProps) => {
  return (
    <CustomLoadingButton
      sx={[
        {
          whiteSpace: 'nowrap',
          backgroundColor: '#006c9c',
          color: '#ffffff',
          height: 34,
          '&:hover': {
            backgroundColor: '#006c9c',
          },
        },
        ...spreadSxProp(sx),
      ]}
      {...buttonProps}
    >
      {title}
    </CustomLoadingButton>
  );
};

type ActionItem =
  | React.ReactElement<typeof GridActionsCellItem>
  | null;

export type ActionsFn = (params: {
  row: WrappedOrganization;
}) => ActionItem[];

interface FhirOrganizationGridProp {
  organizations: WrappedOrganization[] | null;
  translate: any;
  searchTextFieldValue?: SearchFieldType;
  onSearchTextFieldChange?: (value: string) => void;
  actions?: ActionsFn;
  filters?: any;
}

const FhirOrganizationGrid = ({
  organizations,
  translate,
  searchTextFieldValue,
  onSearchTextFieldChange,
  actions,
  filters
}: FhirOrganizationGridProp) => {

  const actionsColumn: GridActionsColDef[] = actions
    ? [
        {
          field: 'actions',
          type: 'actions',
          headerName: '',
          flex: 0.5,
          getActions: ({ row }) => {
            const items = actions({ row: row }) || [];

            return items.filter(Boolean) as React.ReactElement[];
          },
        },
      ]
    : [];

  const columns: GridColumns = [
    {
      field: 'name',
      headerName: `${translate('details.child.name')}`,
      valueGetter: (params: GridValueGetterParams) => {
        const name = `${params.row?.name}`;
        return name;
      },
      editable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      flex: 2
    },
    {
      field: 'type',
      headerName: `${translate('details.child.type')}`,
      renderCell: (params: GridRenderCellParams) => {
        const type = params?.row?.type?.[0] ?? null;
        return (
          <Typography variant="body2">
            {getCodeableConceptDisplayName(type, true)}
          </Typography>
        )
      },
      editable: false,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      flex: 2,
    },
    ...actionsColumn
  ];
  
  return (
    <DataGridWithFilters
      addButtonTitle=""
      onAddButtonClick={undefined}
      rows={organizations ?? []}
      columns={columns}
      getRowId={(row) => uuidv4()}
      showPagination={true}
      autoHeight={true}
      showIconFilterDrawer={false}
      onSearchTextFieldChange={onSearchTextFieldChange}
      searchTextFieldValue={searchTextFieldValue}
      rowsPerPageOptions={[5,10]}
      childrenButtons={filters}
      Filters={filters ? null : <></>}
    />
  )
}

export default FhirOrganizationGrid;

export const filterOrgBySearchInput = (
  searchInput: string,
  organization: WrappedOrganization
): boolean => {
  if (!searchInput || searchInput.length < 3) return true;

  const searcheableValues: string[] = [
    organization?.getType() ?? '',
    organization?.name ?? '',
  ];

  for (const value of searcheableValues) {
    if (value.toLowerCase().includes(searchInput)) return true;
  }

  return false;
};
